"use client";

import { useCallback } from "react";

import Image from "next/image";

import { ArrowRight, Info } from "lucide-react";

import { renderTemplate } from "@repo/ui/components/common/cms";
import { isFullFlexBundle, isSmartBundle } from "@repo/ui/constants/bundleCodes";
import { DIRECTION_LABELS, DIRECTIONS } from "@repo/ui/constants/directions";
import { SSR_CODES } from "@repo/ui/constants/ssrs";
import { formatTravelHeader } from "@repo/ui/helpers/Extras/formatTravelHeader";
import type { CommonInfoSection, FlexiSmartCardSection } from "@repo/ui/types/cms";
import type { JourneyWithPassengerSegments } from "@repo/ui/types/extras";

import { useAppSelector } from "@repo/store/hooks";
import { useBookingDetails } from "@repo/store/slices";

import ExtrasAccordion from "../common/ExtrasAccordion";
import { FlexibilityCard } from "./FlexibilityCard";
import FlexiSmartBanner from "./FlexiSmartBanner";

interface FlexiSmartSectionCardProps {
  journeys?: JourneyWithPassengerSegments[];
  cmsData?: FlexiSmartCardSection;
  commonInfo?: CommonInfoSection;
  addedButtonText?: string;
}

export default function ExtrasFlexiSmartSectionCard({
  journeys = [],
  cmsData,
  commonInfo,
  addedButtonText,
}: Readonly<FlexiSmartSectionCardProps>) {
  const bookingDetails = useBookingDetails();
  const stations = useAppSelector((state) => state.stations?.stations);

  const { headerImage, headerIcon, headerTitle, headerSubtitle, accordion } = cmsData || {};

  const renderJourneyCard = useCallback(
    (journey: JourneyWithPassengerSegments, index: number) => {
      const direction = index === 0 ? DIRECTIONS.OUTBOUND : DIRECTIONS.INBOUND;

      const directionLabel =
        direction === DIRECTIONS.OUTBOUND
          ? commonInfo?.outboundLabel || DIRECTION_LABELS.OUTBOUND
          : commonInfo?.returnLabel || DIRECTION_LABELS.INBOUND;

      const headerLocations = formatTravelHeader(bookingDetails, direction, stations);

      const firstPassenger = Object.values(journey.passengerSegment)[0];
      const pbdSSR = firstPassenger?.ssrs?.find(
        (ssr) => ssr.ssrCode?.toLowerCase() === SSR_CODES.FLEXI.toLowerCase(),
      );

      const price = pbdSSR?.price ?? 0;
      const showButton = !pbdSSR?.inBundle;

      const subtitle = (
        <span className="flex items-center gap-1">
          <span className="font-semibold text-brand-red">{directionLabel}</span>
          {headerLocations.headerOrigin}
          <ArrowRight className="w-4 h-4" />
          {headerLocations.headerDestination}
        </span>
      );

      const baseCardProps = {
        price,
        direction,
        subtitle,
        showButton,
        cmsData,
        addedButtonText,
      };

      const renderPackSmartInfo = () => (
        <div className="flex items-center justify-center gap-1 p-1 text-sm text-white border rounded-md bg-brand-cyan border-brand-cyan">
          <Info className="w-4 h-4" aria-hidden />
          <span className="flex flex-row items-center">
            {renderTemplate(cmsData?.packSmartLabel ?? "", {
              packSmartIcon: (
                <Image src={cmsData?.packSmartIcon ?? ""} width={80} height={35} alt="Pack Smart" />
              ),
            })}
          </span>
        </div>
      );

      const renderPackFullInfo = () => (
        <div className="flex items-center justify-center gap-1 p-1 text-sm text-white border rounded-md bg-brand-blue border-brand-blue">
          <Info className="w-4 h-4" aria-hidden />
          <span className="flex flex-row items-center">
            {renderTemplate(cmsData?.packFullLabel ?? "", {
              packFullIcon: (
                <Image src={cmsData?.packFullIcon ?? ""} width={80} height={35} alt="Pack Full" />
              ),
            })}
          </span>
        </div>
      );

      const renderCard = () => {
        if (isSmartBundle(journey.bundleCode)) {
          return (
            <FlexibilityCard
              {...baseCardProps}
              id={`journey-${index}${direction}-BND1`}
              title={cmsData?.alreadyHaveFlexibilityLabel ?? "¡Flexibilidad incluida!"}
              infoText={renderPackSmartInfo()}
              showPrice={false}
            />
          );
        } else if (isFullFlexBundle(journey.bundleCode)) {
          return (
            <FlexibilityCard
              {...baseCardProps}
              id={`journey-${index}${direction}-BND2`}
              title={cmsData?.alreadyHaveFlexibilityLabel ?? "¡Flexibilidad Premium incluida!"}
              infoText={renderPackFullInfo()}
              showPrice={false}
            />
          );
        } else {
          return (
            <FlexibilityCard
              {...baseCardProps}
              id={`journey-${index}${direction}`}
              title={cmsData?.wantFlexibilityLabel ?? "¡Agrega FlexiSmart a tu viaje!"}
              infoText={null}
              showPrice
            />
          );
        }
      };

      return (
        <div
          key={`journey-${index}-${journey.journeyKey}`}
          className={journeys.length === 1 ? "w-full md:w-1/2 max-w-[600px]" : "w-full xl:w-1/2"}
          data-testid={`jorney-${journey?.journeyKey}-card`}
        >
          {renderCard()}
        </div>
      );
    },
    [bookingDetails, cmsData, commonInfo, addedButtonText, journeys.length, stations],
  );

  return (
    <div
      className="flex flex-col w-full p-5 mb-3 overflow-hidden bg-white shadow rounded-xl"
      data-testid="extra-fly-smart-section"
    >
      <div className="border rounded-xl">
        <FlexiSmartBanner
          banner={headerImage}
          icon={headerIcon}
          title={headerTitle || "FlexiSmart"}
          subtitle={
            headerSubtitle || "Last-minute problems? Forget about them with our flexibility!"
          }
        />

        <div className="flex flex-col w-full p-5 space-y-4">
          <div className="p-2">
            <ExtrasAccordion extrasAccordionContent={accordion?.[0]?.items || []} />
          </div>

          <div
            className={`flex flex-col w-full gap-4 ${journeys.length === 1 ? "items-center" : "xl:flex-row"}`}
            data-testid="extra-jorneys-section"
          >
            {journeys.length > 0
              ? journeys.map((journey, index) => renderJourneyCard(journey, index))
              : null}
          </div>
        </div>
      </div>
    </div>
  );
}
